import React from "react";

import CountResult from "common/entities/CountResult";
import XHRResolverService from "core/service/resolver/impl/XHRResolver.service";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import { BlockView } from "common/views/block/BlockView.view";
import PagingView from "common/views/paging/Paging.view";
import DataTable from "common/views/table/DataTable";
import DictionaryUtil from "core/util/dictionary/DictionaryUtil";
import Query from "core/api/QueryComponent";
import { IPanelProps, Panel } from "core/decorators/Panel";
import StringUtil from "core/util/StringUtil";
import { addEnum, Field, generateQuery, QueryType } from "typegraph";
import NonIdealState from "../../common/fragments/NonIdealState";
import Icon from "common/views/icon/Icon";
import { Subscription } from "./entities/Subscription";
import * as columns from "./fragments/subscriptionFragments";
import labelProvider from './Subscriptions.labels';
import { SubscriptionsConfig } from "./SubscriptionsConfig";
import { IDataTableColumn } from "common/views/table/DataTable";
import { ButtonView,ButtonVariant } from "common/views/button/Button.view";
import ServiceRegistry from "core/util/ServiceRegistry";
import IResolverService from "core/service/resolver/IResolverService";
import Logger from "core/util/Logger";
import './Subscriptions.css'
import Link from "common/views/pageLink/Link";
import { TextInputVariant, TextInputView } from "common/views/textInput/TextInput.view";
import PickLabels from "core/components/label/PickLabels";
import PageSize from "common/fragments/PageSize";
import { FlexRight } from "common/fragments/Flex";
import TypographyView from "common/views/typographyView/Typography.view";
import initialState from "core/reducers/initialState";
import { dictionaryAddEntries } from "core/actions/dictionaryActions";
import DictionaryEntry from "core/service/localization/entities/DictionaryEntry";
import TokenSession from "core/util/session/TokenSession";
import { findDOMNode } from "react-dom";

const { Labels } = labelProvider;

interface ISubscriptionsParams {
	completions?: any[];
	pageSize?: number;
	sort?: string;
}

const completions = {
	NotStarted: addEnum('NotStarted'),
	Started: addEnum('Started'),
	Incomplete: addEnum('Incomplete'),
	Completed: addEnum('Completed'),
	Passed: addEnum('Passed'),
	Failed: addEnum('Failed')
};

interface ISubscriptionsState {
	pageNumber: number;
	pageSize: number;
	sort: string;
	search: string;
	forceRefresh: any;
}

function mapParams(params: any) {
	return { params };
}

class SubscriptionsQuery implements IPageableListQuery<Subscription> {

	@Field({
		entity: Subscription,
		params: mapParams,
		aliasFor: 'subscriptionsUser'
	})
	public entities: null | Subscription[];

	@Field({
		entity: CountResult,
		params: mapParams,
		aliasFor: 'subscriptionsUserCount'
	})
	public count: CountResult;
}



@Panel({id: "subscriptions-table", configClass: SubscriptionsConfig, pageParams: true, labelProvider})
export class SubscriptionsPanel extends React.PureComponent<IPanelProps<SubscriptionsConfig>, ISubscriptionsState> {
	public state = { search: this.props.searchText, pageNumber: null, pageSize: null, sort: '', forceRefresh : null };

	private handleChange = (val: string) => {
		if (this.props.searchText && !val){
			this.props.actions.resetProgramPageParams('searchText');
		}
		this.setState({ search : val});
	}

	private onSubmit = (e?: any) => {
		if (e) {
			e.preventDefault();
		}
		if(this.state.search) {
			this.props.actions.pushPageParams({key: 'searchText', value: this.state.search}, {key: 'pageNumber', value: 0});
		}
		
	}
	private onClear = () => {
		this.setState({ search : ''});
		this.props.actions.resetProgramPageParams('searchText');
	}

	componentDidUpdate(prevProps, prevState) {
		const { config } = this.props;
		
		ServiceRegistry.get(IResolverService).clearCache()
			.then(() => {
				if (this.state.forceRefresh) {
					this.state.forceRefresh();
				}
			})
			.catch(err => Logger.error(err));
        
	}

	componentWillUnmount(): void {
		this.props.actions.resetProgramPageParams('searchText');
	}

	private getSearchBar = () => {
		const { search } = this.state;
		const { searchText } = this.props;
		return (
			<div id="searchBar" onKeyPress={(event)=> event.key === "Enter" ? this.onSubmit() : null}>
				<TextInputView
						viewKey="searchbar"
						variant={TextInputVariant.SEARCH}
						value={search}
						onChange={this.handleChange}
						style={{flexGrow: 1}}
						ariaLabel="searchBar"
					/>
				{!!searchText ? (
					<ButtonView onClick={()=>this.onClear()} viewKey="clear">
						<Icon icon="cross" />
					</ButtonView>
				) : (
					<ButtonView onClick={()=>this.onSubmit()} viewKey="go">
						<Icon icon="search" />
					</ButtonView>
				)}
			</div>
		)
	}

	refresh = async (forceRefresh) => {
		await ServiceRegistry.get(IResolverService).clearCache();
		 if (forceRefresh) {
            forceRefresh(); // Make sure forceRefresh exists
        }
	}

	_export = async (pageParams) => { 
		 const resolver = ServiceRegistry.get(IResolverService);

		 let completedYear = pageParams.completedYear || "";
		 const subscription = this.props.config.subscriptionParams;
		 let excludeFromTranscript = subscription != undefined ? subscription['excludeFromTranscript']:false;
		const cols = Object.entries(columns).filter(([key, value]) => {
			if (this.props.config.subscriptionComponent.meta && this.props.config.subscriptionComponent.meta.includes(key)) {
				return true;
			}
		}).map(([k, v]) => v);

		const labels = cols.filter(item => item.label)  // Getting column labels
			.map(item => (
				{
				key: item.label['k'],
					value: item.label['v'],
			}));

		let dictionary = null;
		let finalColLabels = null;
		await ServiceRegistry.get(XHRResolverService)
			.execute(`query { dictionaryEntry_many(params: {}) { key, value, instance } }`)
			.then((res) => {
				const response = typeof res === "string" ? JSON.parse(res) : res;
				dictionary = response.data ? response.data.dictionaryEntry_many : [];
				const keysToFind = labels;
				const instance = this.props.instance || "";
				const getValuesForKeysWithInstance = (dictionary, keysToFind, instance) => {
					const entries = Array.isArray(dictionary) ? dictionary : [];
					return keysToFind.map(({ key, value }) => {
						const match = entries.find(entry => entry.key === key && entry.instance === instance);
						return { key, value: match ? match.value : value };
					});
				};
				finalColLabels = dictionary.length ? getValuesForKeysWithInstance(dictionary, keysToFind, instance) : labels;
			}).catch((err) => (Logger.error("Error in .catch:", err)));


		if(excludeFromTranscript === undefined){
			excludeFromTranscript = false;
		}
		if (finalColLabels) {
		 return resolver.execute(
			`{
				export(params: {
				id: "subscription",
				completedYear : ${JSON.stringify(completedYear)},
				excludeFromTranscript : ${excludeFromTranscript},
				sort:"${ pageParams['subscription.sort'] || subscription['sort'] }"
				labels:${JSON.stringify(finalColLabels).replace(/"([^"]+)":/g, '$1:')}
		 		})
			}`
		).then((res) => {
			if(res.data && res.data.export){
				const linkSource = `data:application/pdf;base64,${res.data.export}`
				const downloadLink = document.createElement("a");
				const fileName = "catalog-grade.pdf";
				downloadLink.href = linkSource;
				downloadLink.download = fileName;
				downloadLink.click();
			}
		}).catch((err) => Logger.error(err));
	}
	}

	public render() {
		
		const { config, pageParams, searchText, actions } = this.props;
		const { search } = this.state;

		const p = Object.entries(config.subscriptionParams || {}).reduce((a, [k, v]) => {

			if (k === 'completions') {
				const clist = [];
				if (Array.isArray(v)) {
					for (const c of v) {
						if (completions[c]) {
							clist.push(completions[c]);
						}
					}
				} else {
					if (completions[v]) {
						clist.push(completions[v]);
					}
				}
				if (clist.length > 0) {
					a[k] = clist;
				}
			} else {
				a[k] = v;
			}

			return a;
		}, {});

		if (pageParams['pageSize']) {
			p['pageSize'] = StringUtil.toInteger(pageParams['pageSize'], p['pageSize']);
		}

		if (!p['pageSize']) {
			p['pageSize'] = 6;
		}

		if (this.props.itemsToShow) {
			p['pageSize'] = this.props.itemsToShow;
		}

		p['pageNumber'] = StringUtil.toInteger(pageParams['pageNumber'], 0);
		if (p['pageNumber'] < 1) {
			p['pageNumber'] = 0;
		}

		p['sort'] = pageParams['subscription.sort'] || p['sort'] || "-startDate";
		p['completedYear'] = pageParams['completedYear'] || "";
		p['itemName'] = this.props.searchText || '';

		return (
			<Query.Root entity={SubscriptionsQuery} args={p}>
				{({loading, data, forceRefresh}) => {

					if(!loading && !searchText && (data == null || data.entities == null || data.entities.length === 0)) {
						return (
							<NonIdealState
								title={<Labels.SubscriptionEmpty/>}
								description={<Labels.SubscriptionEmptyDescription/>}
							/>
						);
					}

					if (forceRefresh && !this.state.forceRefresh) {
						// Set the forceRefresh only if it's available and hasn't been set yet
						this.setState({ forceRefresh });
					}

					const lst = data && data.entities;

					const cols = Object.entries(columns).filter(([key, value]) => {
						if(config.subscriptionComponent.meta && config.subscriptionComponent.meta.includes(key)){
							return true;
						} 
					}).map(([k, v]) => v);

					// pageParams.labels = [];

					return (
						<React.Fragment>
							{
								config.export && (
								<div id="downloadTranscript">
									<ButtonView
											viewKey="export.button"
											onClick={() =>this._export(pageParams)}
											>
												Download Transcript
									</ButtonView>
								</div>
								
								)
							}
							<div  style={{display:"flex", justifyContent:"space-between"}}>
								{
									<PickLabels labels={{'search.placeholder': "Search"}}>
									{
										() => (
										this.getSearchBar()
										)
									}
									</PickLabels>
								}
								<TypographyView viewKey="info.title" style={{padding: "12px",background: "aliceblue"}}>
									<span style={{marginRight : "5px"}}><Icon icon="info-sign"/></span> 
									{
										document.querySelectorAll(".__menu-item.active") && document.querySelectorAll(".__menu-item.active")[1] && document.querySelectorAll(".__menu-item.active")[1].textContent? `Hit ${document.querySelectorAll(".__menu-item.active")[1].textContent} tab, to refresh the data` : "Hit selected tab, to refresh the data"
									}
									
								</TypographyView>
							</div>
							{
								config.showTablePageSize ? (
									<FlexRight>
										<PageSize
											id={'subsription.pageSize'}
											instance={'mylearning'}
											searchText={this.props.searchText}
											pageSizeOnLoad={20}
											pageNumber={p['pageNumber']}
											setPageNumberToZero={()=>actions.pushPageParam('pageNumber', '0')}
										/>
									</FlexRight>
								) : ''
							}

							 { (search && (!lst || !lst.length)) ? (
								<NonIdealState
								  	title={<Labels.SubscriptionNoResults/>}
								    description={<Labels.SubscriptionNoResultsDescription/>}
								/>
							 ) : (
								<DataTable
							    	viewKeyPrefix="results"
								  	setSort={(sort) => actions.pushPageParam('subscription.sort',sort)}
								  	data={lst}
							      	params={p}
								  	entity={Subscription}
								  	getId={(sub) => sub.id}
								  	columns={Object.values(cols)}
								  	getColumnProps={(dataItem: Subscription): any => ({subscription: dataItem, config:config})}
								/>
							 )
							}
							<BlockView viewKey="paging.block">
								<PagingView
									viewKey="paging"
									setPageNumber={(pageNumber) => actions.pushPageParam('pageNumber', pageNumber.toString())}
									total={data && data.count.count || 0}
									pageNumber={p['pageNumber']}
									pageSize={p['pageSize']}
								/>
							</BlockView>
						</React.Fragment>
					);
				}}
			</Query.Root>
		);
	}
}




// WEBPACK FOOTER //
// ./src/panels/subscriptions/SubscriptionsTable.panel.tsx